import {Utils} from "./Utils";
import * as moment from "moment";
import {IStateProperty} from "../../BaseColumn";
import _ from "lodash";
import {FlagDto, OSPGenerationDto, OptionBasicCoreDto, OptionBasicDtoOptionType} from "../../../services/Client";
import {
    EColumnKey,
    MissingPDiPAMTBsKey,
    PDiPExcludedOptionTypes,
    SpecificFilterEnum
} from "../../../views/OptionsView/OptionsViewUtils";
import {PDiPFlag, PCCDFlag} from "../VirtualizedTableUtils";
import {EVirtualizedTableCellType, PDiPFilterEnum, PCCDFilterEnum} from "../VirtualizedTableEnums";
import {
    IDataTwoLanguageInput,
    IVirtualizedTableColumn,
    ISelectFieldValue,
    TData,
    TDerivate,
    IOBReference, IOptionIndicator, AssignmentTypeFilter
} from "../VirtualizedTableInterfaces";
import {EOptionState} from "../../DerivateOptionBar";

export class FilterHandler {
    private tableUtils: Utils;

    constructor() {
        this.tableUtils = new Utils();
    }

    filterData(data: TData[], filterValue: object, rows: IVirtualizedTableColumn[]) {
        const filterKeysWithValue = this.buildFilterKeysWithValue(filterValue);
        const keyList: string[] = [];
        for (const key in filterValue) {
            if (filterValue.hasOwnProperty(key)) {
                const element = filterValue[key];
                if (element)
                    keyList.push(key);
            }
        }
        if (keyList.length === 0)
            return data;
        return data.filter((item: TData) => {
            const filterKeysStates: boolean[] = this.getFilterKeysStates(filterKeysWithValue, rows, filterValue, item);

            return filterKeysStates.findIndex((val) => !val) === -1;
        });
    }

    filterPSLData(
        data: TData[],
        filterValue: object,
        rows: IVirtualizedTableColumn[],
        assignmentTypeFilterValue: AssignmentTypeFilter,
        visibleDerivates: Set<string>,
        specificTypeOptionFilter: SpecificFilterEnum,
        compareAssignmentsFilterKeys: number[],
        hideEmptyAssignmentsRowsAndColumns: boolean,
        selectedIdList: string[] = [],
        isShowFilterData:boolean,
        dynTableSelectedIdList: string[] = []
    ) {

        const filterKeysWithValue = this.buildFilterKeysWithValue(filterValue);
        const keyList: string[] = [];
        for (const key in filterValue) {
            if (filterValue.hasOwnProperty(key)) {
                const element = filterValue[key];
                if (element) keyList.push(key);
            }
        }

        // no active filter
        if (keyList.length === 0
            && assignmentTypeFilterValue.filterValues.size === 0
            && assignmentTypeFilterValue.missingPDiPAsgn !== true
            && specificTypeOptionFilter === SpecificFilterEnum.SHOW_ALL
            && compareAssignmentsFilterKeys.length < 2
            && !hideEmptyAssignmentsRowsAndColumns
            && (selectedIdList.length === 0 || !isShowFilterData || dynTableSelectedIdList.length === 0)
        ) {
            return data;
        }

        return data.filter((item: TData,index: number) => {
            const filterKeysStates: boolean[] = this.getFilterKeysStates(filterKeysWithValue, rows, filterValue, item);

            if (specificTypeOptionFilter !== SpecificFilterEnum.SHOW_ALL) {
                filterKeysStates.push(
                    (specificTypeOptionFilter === SpecificFilterEnum.SHOW_PDIP_OPTIONS && (item[PDiPFlag] as boolean)) ||
                    ((specificTypeOptionFilter === SpecificFilterEnum.SHOW_BM_OPTIONS ||
                            (specificTypeOptionFilter === SpecificFilterEnum.SHOW_PDIP_SYNC_ERROR_OPTIONS &&
                                !PDiPExcludedOptionTypes.has(item[EColumnKey.OptionTypeKey] as OptionBasicDtoOptionType)))
                        && !(item[PDiPFlag] as boolean))
                );
            }

            const derivateStatesObjectKeys = Object.keys((item.derivateStates as TDerivate));

            if (hideEmptyAssignmentsRowsAndColumns
                && (derivateStatesObjectKeys.length === 0
                    || !derivateStatesObjectKeys.find(derivateKey => visibleDerivates.has(derivateKey)))
            ) {
                return false;
            }

            if (assignmentTypeFilterValue.filterValues.size > 0) {
                // filter based on assignment types or empty
                const emptyAssignments = assignmentTypeFilterValue.filterValues.has(EOptionState.NotAvailable);
                const assignmentTypes = new Set(assignmentTypeFilterValue.filterValues);
                if (emptyAssignments) {
                    assignmentTypes.delete(EOptionState.NotAvailable);
                }

                if (assignmentTypes.size > 0 && emptyAssignments) {
                    filterKeysStates.push(this.hasAssignmentType(visibleDerivates, derivateStatesObjectKeys, assignmentTypes, item)
                        || this.hasEmptyAssignment(derivateStatesObjectKeys));
                } else if (assignmentTypes.size > 0) {
                    filterKeysStates.push(this.hasAssignmentType(visibleDerivates, derivateStatesObjectKeys, assignmentTypes, item));
                } else if (emptyAssignments) {
                    filterKeysStates.push(this.hasEmptyAssignment(derivateStatesObjectKeys));
                }

            } else if (assignmentTypeFilterValue.missingPDiPAsgn === true) {
                filterKeysStates.push(derivateStatesObjectKeys.find(derivateKey => visibleDerivates.has(derivateKey) &&
                    (item[MissingPDiPAMTBsKey] as Set<string>).has(derivateKey)) !== undefined);
            }

            if (compareAssignmentsFilterKeys.length > 1) {
                // true if the selected derivates have different assignment types
                let result = false;
                let derivateKeys = [...compareAssignmentsFilterKeys];
                // @ts-ignore
                const firstAssignmentType = (item.derivateStates as TDerivate)[derivateKeys.pop()];

                for (const key of derivateKeys) {
                    if ((item.derivateStates as TDerivate)[key as number] !== firstAssignmentType) {
                        result = true;
                        break;
                    }
                }

                filterKeysStates.push(result);
            }
            if (selectedIdList.length > 0 && isShowFilterData) {
                // true if the item is in the selectedIdList
                filterKeysStates.push(selectedIdList.includes(index.toString()));
            }
            return filterKeysStates.findIndex((val) => !val) === -1;
        });
    }

    private getFilterKeysStates(filterKeysWithValue: string[], rows: IVirtualizedTableColumn[], filterValue: object, item: TData) {
        return filterKeysWithValue.map((key) => {
            return this.mapFilterKeysState(key, rows, filterValue, item);
        });
    }

    private mapFilterKeysState(key: string, rows: IVirtualizedTableColumn[], filterValue: object, item: TData) {
        const rowData = this.tableUtils.getTableRowByKey(key, rows);

        // quick filters with multiple selection
        if (filterValue[key] instanceof Set) {
            if (filterValue[key].size > 0) {
                if (rowData.rowType === EVirtualizedTableCellType.WORKFLOWSTATE) {
                    const cellData: any = item[key];
                    if (cellData === undefined
                        || cellData.statusValueState === undefined
                        || cellData.statusValueStates === undefined) {
                        return false;
                    }
                    const workflowStatusId = cellData.statusValueState;
                    const workflowStatusName = cellData.statusValueStates
                        .find((elem: IStateProperty) => elem.id === workflowStatusId)
                        .hoverText.split(" - ")[0];

                    return filterValue[key].has((workflowStatusName as string));
                } else if (rowData.rowType === EVirtualizedTableCellType.DROPDOWN && rowData.key === 'categoryText') {
                    return filterValue[key].has((item[key] as ISelectFieldValue).categoryText);
                } else if (rowData.rowType === EVirtualizedTableCellType.DROPDOWN && rowData.key === 'optionType') {
                    return filterValue[key].has((item[key] as ISelectFieldValue).optionType);
                }
                 else if (rowData.rowType === EVirtualizedTableCellType.FLAG) {
                    // @ts-ignore ts complains that value property does not exists, but it does
                    if (item.flags && item.flags.value && item.flags.value.length > 0) {
                        // @ts-ignore
                        return !!item.flags.value.find((flag: FlagDto) => filterValue[key].has(flag.textLabel));
                    } else {
                        return false;
                    }
                } else {
                    return filterValue[key].has(item[key]);
                }
            } else {
                return true;
            }
        } else if (key === PDiPFlag) {
            if (Boolean(item[key]).valueOf() !== (filterValue[key] === PDiPFilterEnum.SHOW_PDIP_VALID)) {
                return false;
            }
        } else if (key === PCCDFlag) {
            if (Boolean(item[key]).valueOf() !== (filterValue[key] === PCCDFilterEnum.SHOW_PCCD_VALID)) {
                return false;
            }
        } else if (rowData.rowType === EVirtualizedTableCellType.TWOLANGUAGEINPUT
            || rowData.rowType === EVirtualizedTableCellType.TWOLANGUAGETEXTAREA
            || rowData.rowType === EVirtualizedTableCellType.TWOLANGUAGECOMPAREINPUT
            || rowData.rowType === EVirtualizedTableCellType.TWOLANGUAGECOMPARETEXTAREA) {
            const cellData = item[key] as IDataTwoLanguageInput;
            for (const cellDataKey in cellData) {
                if (cellData.hasOwnProperty(cellDataKey)) {
                    const element = cellData[cellDataKey];
                    const stringValue = element.value.toString().toLowerCase();
                    const filterValueString = filterValue[key].toString().toLowerCase();
                    const stringIndex = stringValue.indexOf(filterValueString);
                    if (stringIndex !== -1)
                        return true;
                }
            }
            return false;
        } else if (rowData.rowType === EVirtualizedTableCellType.DROPDOWN) {
            const cellData = item[key] as ISelectFieldValue;
            if (cellData.categoryText) {
                if (cellData.categoryText.toLocaleLowerCase().indexOf(filterValue[key].toString().toLocaleLowerCase()) === -1) {
                    return false;
                }
            } else if (cellData.typefamilyName) {
                if (cellData.typefamilyName.toLowerCase().indexOf(filterValue[key].toString().toLowerCase()) === -1) {
                    return false;
                }
            } else if (!cellData.value || cellData.value.toString().toLowerCase().indexOf(filterValue[key].toString().toLowerCase()) === -1) {
                return false;
            }
        } else if (rowData.rowType === EVirtualizedTableCellType.ONCLICKINPUT) {
            if (key === EColumnKey.ConstraintKey || key === EColumnKey.ExclusionKey || key === EColumnKey.PackageContentKey) {
                const cellData = item[key] as OptionBasicCoreDto[];
                const mappedCellData = cellData && cellData.length > 0 ?
                    cellData.map((ob: OptionBasicCoreDto) => ob.pccdPackageContent).join(", ") : "";

                if (mappedCellData.toLowerCase().indexOf(filterValue[key].toString().toLowerCase()) === -1) {
                    return false;
                }
            }
        } else if(rowData.rowType === EVirtualizedTableCellType.GENERATION) {
            if(item.generation) {
                const filterValueString = filterValue[key].toString().toLowerCase();
                const generationValueString = (item[key] as OSPGenerationDto).generation.toLowerCase();
                const stringIndex = generationValueString.indexOf(filterValueString);
                if (stringIndex !== -1)
                    return true;
            }
            return false;
        } else if (rowData.rowType === EVirtualizedTableCellType.DATE) {
            const cellData = item[key] as Date;
            if (_.isEmpty(cellData))
                return false;
            const dateString = moment(cellData).format('DD.MM.YYYY');
            if (dateString.toLowerCase().indexOf(filterValue[key].toString().toLowerCase()) === -1)
                return false;
        } else if (rowData.rowType === EVirtualizedTableCellType.WORKFLOWSTATE) {
            const cellData: any = item[key];
            if (_.isEmpty(cellData))
                return false;
            const workflowStatusId = cellData.statusValueState;
            const workflowStatusName = cellData.statusValueStates.find((elem: IStateProperty) => elem.id === workflowStatusId).hoverText;
            if (((workflowStatusName).toString() as string).toLowerCase().indexOf(filterValue[key].toString().toLowerCase()) === -1)
                return false;
        } else if (rowData.rowType === EVirtualizedTableCellType.DERIVATE) {
            const cellData = item[key] as TDerivate;
            if (_.isEmpty(cellData))
                return false;
            if (!_.find(cellData, (cvalue, ckey) => _.get(filterValue[key], ckey) === cvalue))
                return false;
        } else if (rowData.rowType === EVirtualizedTableCellType.REFERENCE) {
            const cellData = item[key] as IOBReference;
            if (cellData === undefined || cellData.optionCount === undefined || filterValue[key] !== cellData.optionCount.toString())
                return false;
        } else if (rowData.rowType === EVirtualizedTableCellType.OPTION_INDICATOR) {
            const cellData = item[key] as IOptionIndicator;
            if (cellData === undefined || cellData.optionCount === undefined || filterValue[key] !== cellData.optionCount.toString())
                return false;
        } else if ((item[key] === 0 ? 0 : item[key] || "").toString().toLowerCase().indexOf(filterValue[key].toString().toLowerCase()) === -1)
            return false;
        return true;
    }

    private buildFilterKeysWithValue(filterValue: object) {
        return Object.keys(filterValue).filter((key) => filterValue[key] !== null && filterValue[key] !== "" && filterValue[key] !== undefined);
    }

    private hasEmptyAssignment(derivateStatesObjectKeys: string[]) {
        return derivateStatesObjectKeys.length === 0;
    }

    private hasAssignmentType(visibleDerivates: Set<string>, derivateStatesObjectKeys: string[], filterValues: Set<EOptionState>, item: TData) {
        return derivateStatesObjectKeys.find(derivateKey => visibleDerivates.has(derivateKey) &&
            filterValues.has((item.derivateStates as TDerivate)[derivateKey])) !== undefined;
    }
}



// WEBPACK FOOTER //
// ./src/components/Table/dataHandler/FilterHandler.ts